<script lang="ts">
import { defineComponent, ref, watch } from "vue";

import { useAlertSearchStore } from "./stores/useAlertSearchStore";
import { useMasterDataStore } from "@/stores/Shared/useMasterDataStore";
import { useAuthStore } from "@/views/PoliceCommandCenter/Authentication/stores/useAuthStore";
import type { $string } from "@/configs/types/Shared/typeShare";
import dayjs from "dayjs";

import ButtonDefaultComponent from "@/components/Forms/Button/ButtonDefaultComponent.vue";
import FormInputDefaultComponent from "@/components/Forms/Input/FormInputDefaultComponent.vue";
import PopupCautionComponent from "@/components/Popups/PopupCautionComponent.vue";
import { useAlertDeleteStore } from "./stores/useAlertDeleteStore";
import BadgeDefaultComponent from "@/components/Forms/Badge/BadgeDefaultComponent.vue";
import PopupDeleteComponent from "@/components/Popups/PopupDeleteComponent.vue";
import PopupSuccessComponent from "@/components/Popups/PopupSuccessComponent.vue";

export default defineComponent({
  name: "AlertView",
  components: {},
  data() {
    return {
      componentName: this.$options.name,
      headers: [
        {
          key: "translations",
          label: this.$t("form.translations.label"),
          parser: (v: any) => {
            return this.$convertString((v.find((t: any) => t?.locale === 'th') || v?.[0])?.title);
          },
        },
        {
          key: "publishedAt",
          label: this.$t("form.publishedAt.label"),
          parser: (v: string) => this.$dayjs(v).format("DD/MM/YYYY HH:mm"),
        },
        { key: "published", label: this.$t("form.published.label") },
        { key: "latitude", label: this.$t("form.latitude.label") },
        { key: "longitude", label: this.$t("form.longitude.label") },
        { key: "area", label: this.$t("form.area.label") },
        { key: "action", label: "" },
      ],
      date: ref([null, dayjs().endOf("d").toDate()]) as any,
    };
  },
  computed: {
    defaultFormComponents() {
      return {
        form: {
          input: {
            keyword: {
              component: FormInputDefaultComponent,
              props: {
                isLabel: false,
                isSpecial: false,
                isVisible: true,
                isError: (this.alertSearchStore.error.keyword || "").length > 0,
                isIcon: false,
                isDisable: this.loading(),
                isRequired: this.alertSearchStore.isRequired.keyword.is,
                textError: this.$t(this.alertSearchStore.isRequired.keyword.placeholder),
                title: this.$t(this.alertSearchStore.isRequired.keyword.label),
                placeholder: this.$t(this.alertSearchStore.isRequired.keyword.placeholder),
                value: this.alertSearchStore.result.keyword,
                name: this.alertSearchStore.isRequired.keyword.name,
              } as typeof FormInputDefaultComponent.props,
              onUpdateValue: (value: $string) => {
                this.alertSearchStore.result.keyword = value;
              },
            },
          },
          button: {
            btnCreate: {
              component: ButtonDefaultComponent,
              props: {
                isSpecial: false,
                isVisible: true,
                isIcon: false,
                isLoading: this.loading(),
                isDisable: this.loading(),
                htmlIcon: "",
                title: this.$t("btn.btn_alert"),
                classEnumName: this.$enums.ENUM_COLOR.DEFAULT_1,
                size: this.$enums.ENUM_SIZE.SMALL,
              } as typeof ButtonDefaultComponent.props,
            },
            btnSearch: {
              component: ButtonDefaultComponent,
              props: {
                isSpecial: false,
                isVisible: true,
                isIcon: false,
                isLoading: this.loading(),
                isDisable: this.loading(),
                htmlIcon: "",
                isSubmit: true,
                title: this.$t("btn.btn_search"),
                classEnumName: this.$enums.ENUM_COLOR.DEFAULT_1,
                size: this.$enums.ENUM_SIZE.SMALL,
              } as typeof ButtonDefaultComponent.props,
            },
            btnReset: {
              component: ButtonDefaultComponent,
              props: {
                isSpecial: false,
                isVisible: true,
                isIcon: false,
                isLoading: this.loading(),
                isDisable: this.loading(),
                htmlIcon: "",
                isSubmit: false,
                title: this.$t("btn.btn_reset"),
                classEnumName: this.$enums.ENUM_COLOR.YELLOW,
                size: this.$enums.ENUM_SIZE.SMALL,
              } as typeof ButtonDefaultComponent.props,
              click: async (e: Event) => {
                this.alertSearchStore.result.type = null;
                this.alertSearchStore.result.keyword = null;
                this.resetDate();
                this.$router.replace({ query: { page: 1 } });
                await this.alertSearchStore.onSubmit(e);
              },
            },
            btnEdit: {
              component: ButtonDefaultComponent,
              props: {
                isSpecial: false,
                isVisible: true,
                isIcon: true,
                isLoading: this.loading(),
                isDisable: this.loading(),
                componentIcon: this.$outlineIcons.PencilSquareIcon,
                classIcon: "ttp-icon-inside-box-03 inline-block text-ttp-primary",
                title: "",
                classEnumName: this.$enums.ENUM_COLOR.NONE,
                size: this.$enums.ENUM_SIZE.SMALL,
              } as typeof ButtonDefaultComponent.props,
            },
            btnDelete: {
              component: ButtonDefaultComponent,
              props: {
                isSpecial: false,
                isVisible: true,
                isIcon: true,
                isLoading: this.loading(),
                isDisable: this.loading(),
                componentIcon: this.$outlineIcons.TrashIcon,
                classIcon: "ttp-icon-inside-box-03 inline-block text-ttp-error",
                title: "",
                classEnumName: this.$enums.ENUM_COLOR.NONE,
                size: this.$enums.ENUM_SIZE.SMALL,
              } as typeof ButtonDefaultComponent.props,
              click: (props: { item: { id: string } }) => {
                this.alertDeleteStore.isDelete = !this.alertDeleteStore.isDelete;
                this.alertDeleteStore.result.id = props?.item?.id as string;
              },
            },
          },
          popup: {
            editCaution: {
              component: PopupCautionComponent,
              props: {
                isOpen: this.alertSearchStore.isOpenComplete,
                textTitle: this.$t("popup.caution.default_title"),
                textDetail: this.$t("popup.caution.completed_detail"),
                functionProp: () => {
                  this.alertSearchStore.isOpenComplete = false;
                },
                functionCancelProp: () => {
                  this.alertSearchStore.isOpenComplete = false;
                },
              } as typeof PopupCautionComponent.props,
            },
            deletePopup: {
              component: PopupDeleteComponent,
              props: {
                isOpen: this.alertDeleteStore.isDelete,
                textTitle: this.$t("popup.error.delete_title"),
                textDetail: this.$t("popup.error.delete_detail"),
                functionProp: async (e: Event) => {
                  this.alertDeleteStore.isDelete = false;
                  await this.alertDeleteStore.onSubmit(e);
                },
                functionCancelProp: () => {
                  this.alertDeleteStore.isDelete = false;
                },
              } as typeof PopupDeleteComponent.props,
            },
            deleteSuccess: {
              component: PopupSuccessComponent,
              props: {
                isOpen: this.alertDeleteStore.isSuccess,
                textTitle: this.$t("popup.success.delete_title"),
                textDetail: this.$t("popup.success.delete_detail"),
                functionProp: () => {
                  this.setting();
                },
                functionCancelProp: () => {
                  this.alertDeleteStore.isSuccess = false;
                },
              } as typeof PopupSuccessComponent.props,
            },
            deleteCaution: {
              component: PopupCautionComponent,
              props: {
                isOpen: this.alertDeleteStore.isSuccess,
                textTitle: this.$t("popup.caution.delete_title"),
                textDetail: this.$t("popup.caution.delete_detail"),
                functionProp: () => {
                  this.setting();
                },
                functionCancelProp: () => {
                  this.alertDeleteStore.isSuccess = false;
                },
              } as typeof PopupCautionComponent.props,
            },
          },
          badge: {
            published: {
              component: BadgeDefaultComponent,
              props: {
                htmlIcon: "",
                isSpecial: false,
                isVisible: true,
                isIcon: true,
                isDisable: false,
                size: this.$enums.ENUM_SIZE.NORMAL,
              } as typeof BadgeDefaultComponent.props,
            },
            isInternal: {
              component: BadgeDefaultComponent,
              props: {
                htmlIcon: "",
                isSpecial: false,
                isVisible: true,
                isIcon: true,
                isDisable: false,
                size: this.$enums.ENUM_SIZE.NORMAL,
              } as typeof BadgeDefaultComponent.props,
            },
          },
        },
      };
    },
  },
  methods: {
    async fetch() {
      await this.alertSearchStore.onUpdateCurrentPage(Number(this.$route.query.page) || 1);
    },
    loading() {
      return this.alertSearchStore.loading;
    },
    async reset() {
      await this.alertSearchStore.$reset();
      await this.alertDeleteStore.$reset();
    },
    async setting() {
      await this.reset();
      await this.resetDate();
      await this.fetch();
      await this.$configLightbox();
    },
    resetDate() {
      this.alertSearchStore.result.startAt = dayjs().startOf("d").toISOString();
      this.alertSearchStore.result.endAt = dayjs().endOf("d").toISOString();

      this.date = ref([this.alertSearchStore.result.startAt, this.alertSearchStore.result.endAt]);
    },
    handleDate(modelData: any) {
      this.date.value = modelData;
      if (!this.date?.value?.length) {
        this.alertSearchStore.result.startAt = dayjs().startOf("d").toISOString();
        this.alertSearchStore.result.endAt = dayjs().endOf("d").toISOString();
      } else {
        const [startAt, endAt] = this.date.value;

        if (startAt) this.alertSearchStore.result.startAt = dayjs(startAt).startOf("d").toISOString();
        else this.alertSearchStore.result.startAt = null;
        if (endAt) this.alertSearchStore.result.endAt = dayjs(endAt).endOf("d").toISOString();
        else this.alertSearchStore.result.endAt = null;
      }
      this.date = ref([this.alertSearchStore.result.startAt, this.alertSearchStore.result.endAt]);
    },
    dateRangeFormat(dates: Date[]) {
      const [start, end] = dates;

      const startString = !start || !dayjs(start).isValid() ? "เริ่มต้น" : dayjs(start).startOf("d").format("DD/MM/YYYY");
      const endString = !end || !dayjs(end).isValid() ? "ปัจจุบัน" : dayjs(end).startOf("d").format("DD/MM/YYYY");

      return `${startString} - ${endString}`;
    },
  },
  async mounted() {
    await this.setting();
  },
  async unmounted() {
    await this.reset();
  },
  created() {
    watch(
      () => this.$route.query.page,
      () => {
        this.fetch();
      }
    );
  },
  setup() {
    const masterDataStore = useMasterDataStore();
    const alertDeleteStore = useAlertDeleteStore();
    const alertSearchStore = useAlertSearchStore();
    const authStore = useAuthStore();

    return {
      masterDataStore,
      alertSearchStore,
      alertDeleteStore,
      authStore,
    };
  },
});
</script>

<template>
  <div class="ttp-form">
    <div class=" items-center justify-end grid grid-cols-6 gap-2">
      <router-link
        :to="{
          name: 'AlertManageView',
        }"
        class="col-end-0 col-span-3 md:col-end-7 md:col-span-2 lg:col-end-7 lg:col-span-1"
      >
        <component classTagName="w-full" :is="defaultFormComponents.form.button.btnCreate.component" v-bind="defaultFormComponents.form.button.btnCreate.props" />
      </router-link>
    </div>
    <form @submit="alertSearchStore.onSubmit">
      <div class="w-full gap-2">
        <div class="ttp-between items-start">
          <div class="grid grid-cols-2 xl:grid-cols-3 gap-2 w-full">
            <VueDatePicker :model-value="date" @update:model-value="handleDate" range :format="dateRangeFormat" :enable-time-picker="false" class="w-full text-left"></VueDatePicker>
            <component :classTagName="'w-full text-left'" :is="defaultFormComponents.form.input.keyword.component" v-bind="defaultFormComponents.form.input.keyword.props" @updateValue="defaultFormComponents.form.input.keyword.onUpdateValue" />
          </div>
          <div class="grid gap-2 grid-cols-2">
            <component :is="defaultFormComponents.form.button.btnReset.component" v-bind="defaultFormComponents.form.button.btnReset.props" @click="defaultFormComponents.form.button.btnReset.click" />
            <component :is="defaultFormComponents.form.button.btnSearch.component" v-bind="defaultFormComponents.form.button.btnSearch.props" />
          </div>
        </div>
      </div>
    </form>
  </div>
  <TableComponent :headers="headers" :content="alertSearchStore.tableResults?.items" :loading="loading()" counter :counter-label="$t('form.number.label')">
    <template v-slot:body-published="props">
      <component :is="defaultFormComponents.form.badge.published.component" v-bind="defaultFormComponents.form.badge.published.props" :title="props?.value == true ? 'แสดง' : 'ไม่แสดง'" :classEnumName="props?.value == true ? $enums.ENUM_COLOR.GREEN : $enums.ENUM_COLOR.RED" />
    </template>
    <template v-slot:body-action="props">
      <router-link
        :to="{
          name: 'AlertManageView',
          params: { id: props?.item?.id },
        }"
      >
        <component :is="defaultFormComponents.form.button.btnEdit.component" v-bind="defaultFormComponents.form.button.btnEdit.props" />
      </router-link>
      <component :is="defaultFormComponents.form.button.btnDelete.component" v-bind="defaultFormComponents.form.button.btnDelete.props" @click="defaultFormComponents.form.button.btnDelete.click(props)" />
    </template>
  </TableComponent>
  <PaginationComponent
    :currentPage="Number($route.query.page) || 1"
    :totalPage="alertSearchStore.totalPage"
    :gotoPage="alertSearchStore.gotoPage"
    :nextPage="alertSearchStore.nextPage"
    :prevPage="alertSearchStore.prevPage"
    :onUpdateCurrentPage="
      () => {
        fetch();
      }
    "
    :totalItem="alertSearchStore.total"
    unitItem="Alerts"
  />

  <component :is="defaultFormComponents.form.popup.editCaution.component" v-bind="defaultFormComponents.form.popup.editCaution.props" />

  <!-- Delete -->
  <component :is="defaultFormComponents.form.popup.deletePopup.component" v-bind="defaultFormComponents.form.popup.deletePopup.props" />
  <component v-if="alertDeleteStore.actionStatus == $enums.ENUM_MUTATION_STATUS.SUCCESS" :is="defaultFormComponents.form.popup.deleteSuccess.component" v-bind="defaultFormComponents.form.popup.deleteSuccess.props" />
  <component v-else-if="alertDeleteStore.actionStatus == $enums.ENUM_MUTATION_STATUS.ERROR" :is="defaultFormComponents.form.popup.deleteCaution.component" v-bind="defaultFormComponents.form.popup.deleteCaution.props" />
</template>
